import React, { useCallback, useState } from 'react';
import { Star, Users, Loader2, EyeOff } from 'lucide-react';

import type { PhotoListItem } from '@/api/types/photo.types';
import { getPhotoThumbnailUrl } from '@/api/services/photo.service';
import { QualityScoreMeter } from './QualityScoreMeter';
import { BestPickBadge } from './BestPickBadge';

interface PhotoCardProps {
  photo: PhotoListItem;
  isSelected: boolean;
  isSelectionMode: boolean;
  onSelect: (photoId: string) => void;
  onClick: (photoId: string) => void;
  onToggleStar: (photoId: string, starred: boolean) => void;
}

const PIPELINE_PROCESSING_STATES = new Set([
  'pending',
  'exif_done',
  'quality_done',
  'faces_done',
  'caption_done',
  'similarity_done',
]);

/**
 * Individual photo card rendered inside the gallery grid.
 * Shows thumbnail, quality badge, face count, star toggle, best pick badge,
 * and processing indicator.
 */
export const PhotoCard = React.memo(
  function PhotoCard({
    photo,
    isSelected,
    isSelectionMode,
    onSelect,
    onClick,
    onToggleStar,
  }: PhotoCardProps) {
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);

    const isProcessing = PIPELINE_PROCESSING_STATES.has(photo.pipeline_status);

    const thumbnailUrl = photo.thumbnail_small
      ? getPhotoThumbnailUrl(photo.id, 'small')
      : undefined;

    const handleClick = useCallback(() => {
      if (isSelectionMode) {
        onSelect(photo.id);
      } else {
        onClick(photo.id);
      }
    }, [isSelectionMode, onSelect, onClick, photo.id]);

    const handleStarClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleStar(photo.id, !photo.is_starred);
      },
      [onToggleStar, photo.id, photo.is_starred],
    );

    const handleSelectClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(photo.id);
      },
      [onSelect, photo.id],
    );

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        className={`group relative cursor-pointer overflow-hidden rounded-lg border transition-all ${
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500/40'
            : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
        }`}
      >
        {/* Thumbnail */}
        <div className="relative aspect-square bg-gray-100 dark:bg-gray-800">
          {thumbnailUrl && !imgError ? (
            <img
              src={thumbnailUrl}
              alt={photo.original_filename}
              loading="lazy"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              className={`h-full w-full object-cover transition-opacity duration-300 ${
                imgLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
              {imgError ? 'Error' : 'No thumbnail'}
            </div>
          )}

          {/* Loading skeleton overlay */}
          {thumbnailUrl && !imgLoaded && !imgError && (
            <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
          )}

          {/* Hidden overlay */}
          {photo.is_hidden && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <EyeOff className="h-6 w-6 text-white/80" />
            </div>
          )}

          {/* Processing indicator */}
          {isProcessing && (
            <div className="absolute bottom-1 left-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                <span className="hidden sm:inline">Processing</span>
              </span>
            </div>
          )}

          {/* Selection checkbox */}
          {isSelectionMode && (
            <button
              onClick={handleSelectClick}
              className="absolute left-1.5 top-1.5 z-10"
            >
              <div
                className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                  isSelected
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-white/80 bg-black/20 text-transparent hover:border-white'
                }`}
              >
                {isSelected && (
                  <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            </button>
          )}

          {/* Star button (top-right) */}
          <button
            onClick={handleStarClick}
            className={`absolute right-1.5 top-1.5 z-10 rounded-full p-1 transition-all ${
              photo.is_starred
                ? 'text-yellow-400'
                : 'text-white/60 opacity-0 hover:text-yellow-400 group-hover:opacity-100'
            }`}
          >
            <Star
              className="h-4 w-4"
              fill={photo.is_starred ? 'currentColor' : 'none'}
            />
          </button>

          {/* Bottom-right badges row */}
          <div className="absolute bottom-1 right-1 flex items-center gap-1">
            {photo.is_group_best && <BestPickBadge />}
            <QualityScoreMeter score={photo.quality_score} />
            {photo.face_count > 0 && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-gray-900/60 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white">
                <Users className="h-2.5 w-2.5" />
                {photo.face_count}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.photo.id === next.photo.id &&
    prev.photo.is_starred === next.photo.is_starred &&
    prev.photo.is_hidden === next.photo.is_hidden &&
    prev.photo.pipeline_status === next.photo.pipeline_status &&
    prev.isSelected === next.isSelected &&
    prev.isSelectionMode === next.isSelectionMode,
);
